import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useRma } from '../hooks/useRma';
import { RmaDetailHeader } from '../components/RmaDetailHeader';
import { RmaItemsTable } from '../components/RmaItemsTable';
import { RmaShippingCard } from '../components/RmaShippingCard';
import { RmaActivityTimeline } from '../components/RmaActivityTimeline';
import { RmaPrintDocument } from '../components/RmaPrintDocument';
import { CreateRmaModal } from '../components/modals/CreateRmaModal';

// =============================================================================
// Loading Skeleton
// =============================================================================

function RmaDetailSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-48" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-6 w-96" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2 space-y-5">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <div className="space-y-5">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// RMA Detail Page
// =============================================================================

export function RmaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('operations');

  // Handle /operations/rma/new route
  const isNew = id === 'new';
  const [createOpen, setCreateOpen] = useState(isNew);

  const rmaQuery = useRma(isNew ? '' : (id ?? ''));

  // Close modal navigates back to list
  const handleCreateClose = (open: boolean) => {
    setCreateOpen(open);
    if (!open && isNew) {
      navigate('/operations/rma');
    }
  };

  // New RMA flow
  if (isNew) {
    return <CreateRmaModal open={createOpen} onOpenChange={handleCreateClose} />;
  }

  // Loading state
  if (rmaQuery.isLoading) {
    return (
      <div className="space-y-5">
        <Link
          to="/operations/rma"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('rma.detail.backToList', 'Back to RMA List')}
        </Link>
        <RmaDetailSkeleton />
      </div>
    );
  }

  // Error state
  if (rmaQuery.error || !rmaQuery.data) {
    return (
      <div className="space-y-5">
        <Link
          to="/operations/rma"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('rma.detail.backToList', 'Back to RMA List')}
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-10 w-10 mx-auto text-destructive/50 mb-3" />
            <p className="text-destructive font-medium">{t('rma.detail.loadFailed', 'Failed to load RMA')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('rma.detail.loadFailedBody', 'The requested RMA could not be found or an error occurred.')}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => rmaQuery.refetch()}
            >
              {t('rma.detail.tryAgain', 'Try Again')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const rma = rmaQuery.data;

  return (
    <>
      {/* Print-only document — hidden on screen, full page when printing */}
      <RmaPrintDocument rma={rma} />

      {/* Screen content — hidden when printing */}
      <div className="space-y-5 print:hidden">
        {/* Back button */}
        <Link
          to="/operations/rma"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('rma.detail.backToList', 'Back to RMA List')}
        </Link>

        {/* Header */}
        <RmaDetailHeader rma={rma} />

        {/* Full-width items table */}
        <RmaItemsTable rma={rma} />

        {/* Shipping + Activity side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <RmaShippingCard rma={rma} />
          <RmaActivityTimeline activity={rma.activity} createdBy={rma.createdBy} />
        </div>
      </div>
    </>
  );
}
